import { DeviceVendor, DeviceVendorDetailsMap } from '@/types/matchDevice';

export interface DeviceHoverSource {
  id?: string | null;
  isEriTracked?: boolean | null;
  vendor?: string | null;
  type?: string | null;
  modelName?: string | null;
  modelNumber?: string | null;
  serialNumber?: string | null;
  implantDate?: string | null;
  deviceImplantDate?: string | Date | null;
  connectivityStatus?: string | null;
  deviceTelemetry?: string | null;
  lastTransmissionAt?: string | null;
  nextTransmissionAt?: string | null;
}

export interface DeviceHoverRow {
  label: string;
  value: string;
  highlight?: boolean;
}

function eriTrackingLabel(value?: boolean | null): { value: string; highlight: boolean } {
  if (value === true) return { value: 'True', highlight: true };
  if (value === false) return { value: 'False', highlight: false };
  return { value: '—', highlight: false };
}

function vendorLabel(vendor?: string | null): string {
  if (!vendor?.trim()) return '';
  return DeviceVendorDetailsMap[vendor as DeviceVendor]?.name ?? vendor.trim();
}

function titled(value?: string | null): string {
  if (!value?.trim()) return '';
  return value
    .trim()
    .split(/[_\s]+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

function displayDate(value?: string | Date | null): string {
  if (!value) return '';
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return '';
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${month}/${day}/${value.getFullYear()}`;
  }
  const text = value.trim();
  const dateOnly = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnly) return `${dateOnly[2]}/${dateOnly[3]}/${dateOnly[1]}`;
  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return text;
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${month}/${day}/${parsed.getFullYear()}`;
}

export function deviceHoverContent(device?: DeviceHoverSource | null): {
  title: string;
  rows: DeviceHoverRow[];
  id: string;
} {
  const title = [vendorLabel(device?.vendor), device?.type?.trim()].filter(Boolean).join(' ');
  const implant = device?.implantDate || device?.deviceImplantDate;
  const eri = eriTrackingLabel(device?.isEriTracked);
  return {
    title: title || 'Device',
    rows: [
      { label: 'Model name', value: device?.modelName?.trim() || '—' },
      { label: 'Model number', value: device?.modelNumber?.trim() || '—' },
      { label: 'Serial number', value: device?.serialNumber?.trim() || '—' },
      { label: 'ERI tracking', value: eri.value, highlight: eri.highlight },
      { label: 'Implant date', value: displayDate(implant) || '—' },
      { label: 'Connectivity status', value: titled(device?.connectivityStatus) || '—' },
      { label: 'Telemetry', value: titled(device?.deviceTelemetry) || '—' },
      { label: 'Last transmission', value: displayDate(device?.lastTransmissionAt) || '—' },
      { label: 'Next transmission', value: displayDate(device?.nextTransmissionAt) || '—' }
    ],
    id: device?.id?.trim() || ''
  };
}
